import PropTypes from 'prop-types';
import React, { useState } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import Link from 'next/link';
import ChevronRightSvg from 'pubweb-smokey/dist/images/svg/iconography-16x16/chevron-right.svg';
import { getSiteUrl } from '@utils/config';
import { getSummary } from '@services/newsroomService';
import { padItems } from '@utils/formatters';
import BrandingCard from '@components/Newsroom/BrandingCard/BrandingCard';
import FeaturedArticles from '@components/Newsroom/FeaturedArticles/FeaturedArticles';
import NewsMentionCard from '@components/Newsroom/NewsMentionCard/NewsMentionCard';
import PressReleaseCard from '@components/Newsroom/PressReleaseCard/PressReleaseCard';
import ResourceCard from '@components/Newsroom/ResourceCard/ResourceCard';
import HomePageStyles from './NewsroomHomePage.styled';
import { prefixRoute } from '@utils/newsroomUtils';

const NewsroomHome = ({ ssrNewsroomSummary }) => {
  const router = useRouter();
  const [newsroomSummary, setNewsroomSummary] = useState(ssrNewsroomSummary);

  if (!newsroomSummary) {
    getSummary()
      .then((result) => {
        setNewsroomSummary(result);
      })
      .catch();
  }

  return (
    <div className="constrained-width-newsroom">
      <HomePageStyles>
        <Head>
          <title>Welcome to the Clayton Newsroom</title>
          <meta
            name="description"
            content=" Clayton has been providing affordable, quality homes since 1956. Learn more about our recent company announcements and publicly released information."
          />
          <link rel="canonical" href={`${getSiteUrl(router?.pathname)}`} />
          <meta
            property="og:image"
            content={`${getSiteUrl('/public-assets/images/clayton-logo.jpg')}`}
          />
        </Head>

        <div className="home-section featured">
          <h3>Featured</h3>
          {newsroomSummary && (
            <FeaturedArticles
              featuredArticles={newsroomSummary?.latestFeaturedArticles}
            />
          )}
        </div>
        <div className="home-section">
          <h3>Press Releases</h3>
          <div className="home-cards">
            {newsroomSummary &&
              newsroomSummary.latestPressReleases.map((pressRelease, index) => {
                return (
                  <PressReleaseCard
                    key={`pr-${index}`}
                    pressRelease={pressRelease}
                  />
                );
              })}
            {padItems(newsroomSummary?.latestPressReleases, 4)}
          </div>
          <Link href={prefixRoute('/press-releases')} className="standard">
            See all press releases
            <ChevronRightSvg />
          </Link>
        </div>
        <div className="home-section">
          <h3>In the News</h3>
          <div className="home-cards">
            {newsroomSummary &&
              newsroomSummary.latestNewsMentions.map((newsMention, index) => {
                return (
                  <NewsMentionCard
                    key={`news-${index}`}
                    newsMention={newsMention}
                  />
                );
              })}

            {padItems(newsroomSummary.latestNewsMentions, 2)}
          </div>
          <Link href={prefixRoute('/in-the-news')} className="standard">
            See all news mentions
            <ChevronRightSvg />
          </Link>
        </div>
        <div className="home-section">
          <h3>Branding</h3>
          <div className="home-cards">
            {newsroomSummary &&
              newsroomSummary.latestBranding.map((branding, index) => {
                return (
                  <BrandingCard key={`branding-${index}`} branding={branding} />
                );
              })}

            {padItems(newsroomSummary.latestBranding, 2)}
          </div>
        </div>
        <div className="home-section">
          <h3>Resources</h3>
          <div className="home-cards">
            {newsroomSummary &&
              newsroomSummary.latestResources.map((resource, index) => {
                return (
                  <ResourceCard key={`resource-${index}`} resource={resource} />
                );
              })}

            {padItems(newsroomSummary.latestResources, 4)}
          </div>
          <Link href={prefixRoute('/resources')} className="standard">
            See all resources
            <ChevronRightSvg />
          </Link>
        </div>
      </HomePageStyles>
    </div>
  );
};

NewsroomHome.propTypes = {
  ssrNewsroomSummary: PropTypes.any,
};

//eslint-disable-next-line no-unused-vars
export async function getServerSideProps() {
  let ssrNewsroomSummary = null;

  await getSummary()
    .then((result) => {
      ssrNewsroomSummary = result;
    })
    .catch((error) => {
      console.error(`Error retrieving Newsroom Homepage: ${error}`);
    });

  return {
    props: {
      ssrNewsroomSummary,
    },
  };
}

export default NewsroomHome;
